import { MFR, OverspecDecision, QA } from '../types/pipeline';
import { parseInlineOverspecs, stripInlineOverspecs } from './stripInlineOverspecs';

// Keep synthetic IDs for old `removed_specs` rows byte-for-byte compatible
// with backend/overspec_decisions.py::legacy_overspec_id.  A legacy flow has no
// persisted decision record until its first explicit save, so its Restore
// action must identify the same read-only projection the server validates.
const SHA256_ROUND_CONSTANTS = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];

function rightRotate(value: number, bits: number): number {
  return (value >>> bits) | (value << (32 - bits));
}

function utf8Bytes(value: string): Uint8Array {
  const bytes: number[] = [];
  for (let index = 0; index < value.length; index += 1) {
    let codePoint = value.charCodeAt(index);
    if (codePoint >= 0xd800 && codePoint <= 0xdbff && index + 1 < value.length) {
      const lowSurrogate = value.charCodeAt(index + 1);
      if (lowSurrogate >= 0xdc00 && lowSurrogate <= 0xdfff) {
        codePoint = 0x10000 + ((codePoint - 0xd800) << 10) + (lowSurrogate - 0xdc00);
        index += 1;
      }
    }
    if (codePoint <= 0x7f) bytes.push(codePoint);
    else if (codePoint <= 0x7ff) bytes.push(0xc0 | (codePoint >>> 6), 0x80 | (codePoint & 0x3f));
    else if (codePoint <= 0xffff) bytes.push(0xe0 | (codePoint >>> 12), 0x80 | ((codePoint >>> 6) & 0x3f), 0x80 | (codePoint & 0x3f));
    else bytes.push(0xf0 | (codePoint >>> 18), 0x80 | ((codePoint >>> 12) & 0x3f), 0x80 | ((codePoint >>> 6) & 0x3f), 0x80 | (codePoint & 0x3f));
  }
  return Uint8Array.from(bytes);
}

/** A small synchronous SHA-256 implementation for deterministic legacy IDs. */
function sha256(value: string): string {
  const input = utf8Bytes(value);
  const paddedLength = Math.ceil((input.length + 9) / 64) * 64;
  const padded = new Uint8Array(paddedLength);
  padded.set(input);
  padded[input.length] = 0x80;
  const bitLength = input.length * 8;
  const view = new DataView(padded.buffer);
  view.setUint32(paddedLength - 8, Math.floor(bitLength / 0x1_0000_0000));
  view.setUint32(paddedLength - 4, bitLength >>> 0);

  const hash = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];
  const words = new Uint32Array(64);
  for (let offset = 0; offset < paddedLength; offset += 64) {
    for (let index = 0; index < 16; index += 1) words[index] = view.getUint32(offset + index * 4);
    for (let index = 16; index < 64; index += 1) {
      const s0 = rightRotate(words[index - 15], 7) ^ rightRotate(words[index - 15], 18) ^ (words[index - 15] >>> 3);
      const s1 = rightRotate(words[index - 2], 17) ^ rightRotate(words[index - 2], 19) ^ (words[index - 2] >>> 10);
      words[index] = (words[index - 16] + s0 + words[index - 7] + s1) >>> 0;
    }
    let [a, b, c, d, e, f, g, h] = hash;
    for (let index = 0; index < 64; index += 1) {
      const sum1 = rightRotate(e, 6) ^ rightRotate(e, 11) ^ rightRotate(e, 25);
      const choice = (e & f) ^ (~e & g);
      const temp1 = (h + sum1 + choice + SHA256_ROUND_CONSTANTS[index] + words[index]) >>> 0;
      const sum0 = rightRotate(a, 2) ^ rightRotate(a, 13) ^ rightRotate(a, 22);
      const majority = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (sum0 + majority) >>> 0;
      [h, g, f, e, d, c, b, a] = [g, f, e, (d + temp1) >>> 0, c, b, a, (temp1 + temp2) >>> 0];
    }
    for (let index = 0; index < hash.length; index += 1) hash[index] = (hash[index] + [a, b, c, d, e, f, g, h][index]) >>> 0;
  }
  return hash.map((part) => part.toString(16).padStart(8, '0')).join('');
}

export function legacyOverspecDecisions(rows: string[]): OverspecDecision[] {
  return rows.map((originalText, occurrence) => ({
    id: `os_legacy_${sha256(`${occurrence}\x00${originalText}`).slice(0, 20)}`,
    originalText,
    state: 'removed',
  }));
}

/** Convert the Stage-2 API/cache shape once, including old string-only flows. */
export function mapOverspecDecisions(value: unknown, legacyRemovedSpecs: string[] = []): OverspecDecision[] | undefined {
  if (!Array.isArray(value)) {
    return legacyRemovedSpecs.length > 0 ? legacyOverspecDecisions(legacyRemovedSpecs) : undefined;
  }
  const decisions: OverspecDecision[] = [];
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const record = item as Record<string, unknown>;
    const id = typeof record.id === 'string' ? record.id : '';
    const originalText = typeof record.original_text === 'string'
      ? record.original_text
      : typeof record.originalText === 'string' ? record.originalText : '';
    if (!id || !originalText) continue;
    decisions.push({
      id,
      originalText,
      state: record.state === 'restored' ? 'restored' : 'removed',
      generatedAt: typeof record.generated_at === 'string' ? record.generated_at : record.generatedAt as string | undefined,
      restoredRequirement: typeof record.restored_requirement === 'string'
        ? record.restored_requirement : record.restoredRequirement as string | undefined,
      restorationReason: typeof record.restoration_reason === 'string'
        ? record.restoration_reason : record.restorationReason as string | undefined,
      restoredAt: typeof record.restored_at === 'string' ? record.restored_at : record.restoredAt as string | undefined,
      restoredBy: typeof record.restored_by === 'string' ? record.restored_by : record.restoredBy as string | undefined,
    });
  }
  // The server treats an empty or malformed decision array beside a current
  // `removed_specs` list as a legacy partial projection. Mirror that behavior
  // so cache, API, and reopened-flow paths all expose actionable rows.
  return decisions.length > 0 || legacyRemovedSpecs.length === 0
    ? decisions
    : legacyOverspecDecisions(legacyRemovedSpecs);
}

export function parseMfrFunctionalRequirements(text: string): string[] {
  const cleanText = stripInlineOverspecs(text || '');
  const numbered = cleanText
    .split(/\n(?=\d+\.\s+)/)
    .map((line) => line.trim())
    .filter((line) => /^\d+\.\s+/.test(line));
  if (numbered.length > 0) return numbered;
  return cleanText.split(/(?<=[.!?])\s+/).map((line) => line.trim()).filter((line) => line.length > 20);
}

export function mapMfrStageOutput(
  output: Record<string, any> | null | undefined,
  context: { id?: string; commodityDescription?: string; questionsAnswered?: QA[] } = {},
): MFR {
  const safe = output ?? {};
  const rawText = safe.minimum_requirements ?? safe.requirements_text ?? safe.rawText ?? '';
  const removedSpecs = Array.isArray(safe.removed_specs)
    ? safe.removed_specs.filter((item: unknown): item is string => typeof item === 'string')
    : Array.isArray(safe.removedSpecs)
      ? safe.removedSpecs.filter((item: unknown): item is string => typeof item === 'string')
      : parseInlineOverspecs(rawText);
  const decisions = mapOverspecDecisions(safe.overspec_decisions ?? safe.overspecDecisions, removedSpecs);
  const requirementsList = Array.isArray(safe.requirements_list)
    ? safe.requirements_list.filter((item: unknown): item is string => typeof item === 'string')
    : parseMfrFunctionalRequirements(rawText);

  return {
    id: context.id ?? safe.id ?? `mfr-${Date.now()}`,
    commodityDescription: safe.original_spec ?? context.commodityDescription ?? '',
    generatedAt: safe.generated_at ?? new Date().toISOString(),
    requirements: { functional: requirementsList, performance: [], environmental: [], integration: [], other: [] },
    questionsAnswered: context.questionsAnswered ?? safe.answers ?? [],
    rawText: stripInlineOverspecs(rawText),
    originalCommodityDescription: context.commodityDescription,
    removedSpecs,
    costSavingsEstimate: safe.cost_savings_estimate ?? safe.costSavingsEstimate ?? '',
    ...(decisions !== undefined ? { overspecDecisions: decisions } : {}),
    ...(typeof (safe.stage2_revision ?? safe.stage2Revision) === 'number'
      ? { stage2Revision: safe.stage2_revision ?? safe.stage2Revision } : {}),
  };
}
